import { ERROR_MESSAGES, REPLY_TEXT_BYTES_HINT, REPLY_TEXT_UNIT_LABELS, type ReplyTextUnit } from '@rp/contracts'

export const REPLY_COPY = {
  title: '回覆',
  editorLabel: '回覆內容',
  placeholder: '輸入回覆內容…',
  accept: '採用建議',
  send: '發送',
  sending: '發送中…',
  sent: '已發送',
  publishing: '發送中，正在等待 Meta 確認…',
  alreadySent: '這則回覆已經送出，系統不會重複發送。',
  alreadyPublishing: '這則回覆已經在送出中，請稍候。',
  claimRequired: '請先認領這個項目再回覆',
  heldByOther: (name: string) => `${name} 正在回覆這個項目，暫時不能發送。`,
  rivalSent: (name: string) => `${name} 已經回覆了這則客人訊息，你的回覆沒有送出`,
  rivalSending: (name: string) => `${name} 正在回覆這位客人，你的回覆沒有送出`,
  rivalSentText: (text: string) => `已送出的回覆：「${text}」`,
  rivalSendingText: (text: string) => `正在送出的回覆：「${text}」`,
  publicDestination: (channel: string, kind: string) => `這則回覆會公開顯示在「${channel}」的${kind}下面`,
  privateDestination: (channel: string, customer: string) =>
    `這則回覆會以私訊傳給${customer}，經由「${channel}」送出，只有對方看得到。`,
  confirmTitle: { comment_thread: '確認公開發送？', dm: '確認發送私訊？' },
  confirmWarning: {
    comment_thread: '發送之後在這裡收不回，要到平台上手動刪除，而客人可能已經看過。',
    dm: '私訊送出後在這裡收不回，客人會直接收到。',
  },
  confirmCancel: '再看看',
  confirmSend: '確認發送',
  confirmNote: '重送會使用同一組識別碼，不會重複發文。',
  corpusNotice: '發送後，這則回覆會成為公司的核准回覆，日後的 AI 建議都會參考它。',
  corpusLink: '查看核准回覆',
  staleDraft: '客人已傳新訊息，請確認草稿是否仍適用',
  deadlineExpired: '已超過 Meta 的 24 小時私訊期限，這則回覆可能會被 Meta 拒絕，但你仍然可以送出試試。',
  characterCount: (count: number, unit: ReplyTextUnit) => `${count} ${REPLY_TEXT_UNIT_LABELS[unit]}`,
  characterCountWithLimit: (count: number, limit: number, unit: ReplyTextUnit) =>
    `${count} / ${limit} ${REPLY_TEXT_UNIT_LABELS[unit]}`,
  overLimit: (over: number, unit: ReplyTextUnit) =>
    unit === 'bytes'
      ? `回覆超過 Instagram 私訊上限 ${over} ${REPLY_TEXT_UNIT_LABELS.bytes}（${REPLY_TEXT_BYTES_HINT}），請縮短後再發送。`
      : `回覆超過上限 ${over} ${REPLY_TEXT_UNIT_LABELS.chars}，請縮短後再發送。`,
  recordedOutcome: {
    as_is: '已記錄為：直接採用',
    light_edit: '已記錄為：小幅修改',
    heavy_edit: '已記錄為：大幅修改',
    ignored: '已記錄為：未採用建議',
  },
} as const

export const FAILURE_COPY = {
  post_deleted: {
    title: '貼文已刪除',
    nextStep: '這則貼文已在 Facebook 或 Instagram 上被刪除，不需要再回覆。',
    actionLabel: null,
    actionKind: null,
  },
  channel_disconnected: {
    title: '粉專已斷線',
    nextStep: '這個粉專的授權已失效，請管理員到「設定」重新連接後再發送。',
    actionLabel: '重新連接',
    actionKind: 'reconnect',
  },
  permission_denied: {
    title: '沒有發送權限',
    nextStep: 'Meta 拒絕了這個粉專的發送權限，你的回覆沒有送出。請管理員到「設定」重新連接粉專，並允許所有權限。',
    actionLabel: null,
    actionKind: null,
  },
  rate_limited: {
    title: 'Meta 暫時限制發送',
    nextStep: '你的回覆沒有送出，請稍等幾分鐘再試一次。',
    actionLabel: '再試一次',
    actionKind: 'retry',
  },
  window_closed: {
    title: '已超過 24 小時回覆期限',
    nextStep: ERROR_MESSAGES.REPLY_WINDOW_CLOSED,
    actionLabel: null,
    actionKind: null,
  },
  unreachable: {
    title: '連不上 Meta',
    nextStep: '這則回覆沒有送出，Meta 沒有收到。請稍後再試一次。',
    actionLabel: '再試一次',
    actionKind: 'retry',
  },
  policy_rejected: {
    title: '內容被 Meta 拒絕',
    nextStep: 'Meta 依政策拒絕了這則回覆，請修改內容後再發送。',
    actionLabel: null,
    actionKind: null,
  },
  unavailable: {
    title: 'Meta 暫時無法使用',
    nextStep: '稍後再試一次；重送會使用同一組識別碼，不會重複發文。',
    actionLabel: '再試一次',
    actionKind: 'retry',
  },
  unknown: {
    title: '發送失敗',
    nextStep: '請再試一次；如果持續失敗，請通知管理員。',
    actionLabel: '再試一次',
    actionKind: 'retry',
  },
} as const

export const CODE_FAILURE_COPY = {
  VALIDATION_FAILED: {
    title: '回覆內容不符合要求',
    nextStep: '伺服器不接受這則回覆的內容，請檢查長度後再發送。',
    actionLabel: null,
    actionKind: null,
  },
  CLAIM_REQUIRED: { title: '需要先認領', nextStep: '請先認領這個項目再發送。', actionLabel: null, actionKind: null },
  CLAIM_HELD_BY_OTHER: {
    title: '認領已被接手',
    nextStep: '這個項目已由其他人處理，請重新整理後再確認。',
    actionLabel: null,
    actionKind: null,
  },
  IDEMPOTENCY_REPLAY_MISMATCH: {
    title: '重送的內容不一致',
    nextStep: '之前已用同一組識別碼送出不同內容，請重新整理後再回覆。',
    actionLabel: null,
    actionKind: null,
  },
  BUDGET_EXHAUSTED: {
    title: '本月用量已達上限',
    nextStep: '請管理員調高預算後再試。',
    actionLabel: null,
    actionKind: null,
  },
  CONFLICT: {
    title: '無法發送',
    nextStep: '這個項目的狀態已改變，請重新整理後再確認。',
    actionLabel: null,
    actionKind: null,
  },
  ALREADY_ANSWERED: {
    title: '這則客人訊息已經有人回覆',
    nextStep: ERROR_MESSAGES.ALREADY_ANSWERED,
    actionLabel: null,
    actionKind: null,
  },
  REPLY_IN_PROGRESS: {
    title: '其他同事正在回覆',
    nextStep: ERROR_MESSAGES.REPLY_IN_PROGRESS,
    actionLabel: null,
    actionKind: null,
  },
  REPLY_WINDOW_CLOSED: {
    title: '已超過 24 小時回覆期限',
    nextStep: ERROR_MESSAGES.REPLY_WINDOW_CLOSED,
    actionLabel: null,
    actionKind: null,
  },
} as const

export const DM_UNCONFIRMED_FAILURE = {
  title: '無法確認私訊是否送達',
  nextStep:
    'Meta 沒有明確回應，這則私訊可能已經送到客人手上。為免客人收到兩次，系統不會再送一次；請到 Meta 收件匣確認後再手動處理。',
  actionLabel: null,
  actionKind: null,
} as const

export const heldByOtherFailure = (name: string) =>
  ({
    title: '其他同事正在回覆',
    nextStep: `${name} 正在回覆這個項目，你的回覆沒有送出。`,
    actionLabel: null,
    actionKind: null,
  }) as const

export const SERVER_MESSAGE_CODES = ['CONFLICT', 'VALIDATION_FAILED'] as const
